import React from 'react';
import { render, Component } from 'preact';
import { getApiUrlByName } from 'project/helpers';
import { apiNames, notificationTypes } from 'project/general';
import { baseApi } from 'project/api';

const GET_QUESTION_URL = getApiUrlByName(apiNames.GET_ASK_ASH_QUESTION);
const TRANSITION_MS = 250;
const FADE_IN_DELAY_MS = 40;

export class AskAshWidget extends Component {
    constructor(props) {
        super(props);

        this.containerRef = null;
        this.hasAutoScrolled = false;
        this.skipAutoLoadBecauseUrlParam = false;
        this.transitionTimer = null;
        this.fadeInDelayTimer = null;

        this.initialState = {
            open: false,
            started: false,
            history: [],
            loading: false,
            error: null,
            displayedNode: null,
            transitioning: false,
            fadeState: 'idle',
        };

        this.state = { ...this.initialState };
    }

    componentWillUnmount() {
        this.clearTransitionTimers();
    }

    scrollToQuestionArea = () => {
        if (!this.containerRef || typeof window === 'undefined') {
            return;
        }

        const yOffset = -20;
        const y = this.containerRef.getBoundingClientRect().top + window.pageYOffset + yOffset;

        window.scrollTo({
            top: y,
            behavior: 'smooth',
        });
    };

    clearTransitionTimers = () => {
        if (this.transitionTimer) {
            clearTimeout(this.transitionTimer);
            this.transitionTimer = null;
        }

        if (this.fadeInDelayTimer) {
            clearTimeout(this.fadeInDelayTimer);
            this.fadeInDelayTimer = null;
        }
    };

    getStepElement = () => {
        if (typeof window === 'undefined') return null;
        return document.querySelector('[data-component-step]');
    };

    setComponentStep = (step) => {
        const el = this.getStepElement();
        if (!el) return;
        el.setAttribute('data-component-step', step == null ? '' : String(step));
    };

    componentDidMount() {
        const questionId = this.getQuestionIdFromUrl();
        if (questionId) {
            this.skipAutoLoadBecauseUrlParam = true;

            this.setState({
                open: true,
                loading: true,
                error: null,
                history: [],
                displayedNode: null,
                transitioning: false,
                fadeState: 'idle',
            }, async () => {
                this.scrollToWidget();

                try {
                    const node = await this.fetchNode({ questionId });

                    this.setState({
                        started: true,
                        history: [node],
                        displayedNode: node,
                        loading: false,
                        error: null,
                        transitioning: false,
                        fadeState: 'idle',
                    }, () => {
                        this.setComponentStep(node.dataComponentStep);
                    });
                } catch (err) {
                    this.setState({
                        started: false,
                        loading: false,
                        error: 'Sorry, something went wrong while loading this step.',
                    }, () => {
                        this.setComponentStep('');
                    });
                }
            });

            return;
        }

        if (this.state.open && this.state.history.length === 0 && this.state.started) {
            this.loadNode();
        }
    }

    componentDidUpdate(_, prevState) {
        if (this.skipAutoLoadBecauseUrlParam) {
            this.skipAutoLoadBecauseUrlParam = false;
            return;
        }

        if (
            this.state.open &&
            this.state.started &&
            this.state.history.length === 0 &&
            (!prevState.open || !prevState.started)
        ) {
            this.loadNode();
        }
    }

    getQuestionIdFromUrl = () => {
        if (typeof window === 'undefined') {
            return null;
        }

        const params = new URLSearchParams(window.location.search || '');
        const questionId = params.get('questionId');

        return questionId && questionId.trim() ? questionId.trim() : null;
    };

    buildNode = (data) => ({
        id: data.questionID,
        question: data.questionText,
        dataComponentStep: data.dataComponentStep,
        answers: (data.availableAnswers || []).map(a => ({
            label: a.answerText,
            answerID: a.answerID,
            answerKey: a.answerKey,
            nextQuestionId: a.nextQuestionId,
            isDeadEnd: a.isDeadEnd,
            url: a.resultsPageUrl,
            isSearchResult: a.isSearchResult
        })),
        breadcrumbs: data.breadcrumbs || [],
        isDeadEnd: data.isDeadEnd,
    });

    setContainerRef = (element) => {
        this.containerRef = element;
    };

    isRestartVariant = () => {
        return (this.props.variant || '').toLowerCase() === 'restart';
    };

    scrollToWidget = () => {
        if (this.hasAutoScrolled || !this.containerRef || typeof window === 'undefined') {
            return;
        }

        this.hasAutoScrolled = true;
        window.requestAnimationFrame(() => {
            this.containerRef.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
    };

    async fetchNode(payload = {}) {
        const apiUrl = `${GET_QUESTION_URL}?questionId=${payload.questionId || ''}`;
        const response = await fetch(apiUrl, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            }
        });

        if (!response.ok) {
            throw new Error(`Request failed with status ${response.status}`);
        }

        const json = await response.json();

        if (!json || !json.success || !json.data) {
            throw new Error('Unexpected API response format');
        }

        return this.buildNode(json.data);
    }

    async loadNode(payload = {}) {
        const { displayedNode } = this.state;
        const isInitialLoad = !displayedNode;

        this.clearTransitionTimers();

        this.setState({
            loading: true,
            error: null,
        });

        try {
            const node = await this.fetchNode(payload);

            if (isInitialLoad) {
                this.setState(prev => ({
                    history: [...prev.history, node],
                    displayedNode: node,
                    transitioning: false,
                    fadeState: 'idle',
                    loading: false,
                    error: null,
                }), () => {
                    this.setComponentStep(node.dataComponentStep);
                });

                return;
            }

            this.setState({
                transitioning: true,
                fadeState: 'out',
                loading: false,
                error: null,
            });

            this.transitionTimer = setTimeout(() => {
                this.setState(prev => ({
                    history: [...prev.history, node],
                    displayedNode: node,
                    fadeState: 'idle',
                    transitioning: true,
                    error: null,
                }), () => {
                    this.setComponentStep(node.dataComponentStep);

                    this.fadeInDelayTimer = setTimeout(() => {
                        this.setState({
                            fadeState: 'in',
                        });

                        this.scrollToQuestionArea();

                        this.transitionTimer = setTimeout(() => {
                            this.setState({
                                transitioning: false,
                                fadeState: 'idle',
                            });
                        }, TRANSITION_MS);
                    }, FADE_IN_DELAY_MS);
                });
            }, TRANSITION_MS);
        } catch (err) {
            this.clearTransitionTimers();

            this.setState({
                loading: false,
                transitioning: false,
                fadeState: 'idle',
                error: 'Sorry, something went wrong while loading this step.',
            }, () => {
                this.setComponentStep('');
            });
        }
    }

    handleSelect = (ans) => {
        if (this.state.transitioning) {
            return;
        }

        if (ans.url) {
            window.location.href = ans.url;
            return;
        }

        this.scrollToQuestionArea();

        this.loadNode({
            questionId: ans.nextQuestionId,
        });
    };

    goBack = () => {
        this.clearTransitionTimers();

        if (this.state.history.length > 1) {
            const newHistory = this.state.history.slice(0, -1);
            const previousNode = newHistory[newHistory.length - 1];

            this.setState({
                history: newHistory,
                displayedNode: previousNode,
                transitioning: false,
                fadeState: 'idle',
                error: null,
                loading: false,
            }, () => {
                this.setComponentStep(previousNode?.dataComponentStep || '');
                this.scrollToQuestionArea();
            });
        }
    };

    startOver = () => {
        this.clearTransitionTimers();
        this.setComponentStep('');

        this.setState({
            history: [],
            displayedNode: null,
            transitioning: false,
            fadeState: 'idle',
            error: null,
            loading: false,
        }, () => {
            this.scrollToQuestionArea();
            this.loadNode();
        });
    };

    closeWidget = (event) => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        this.clearTransitionTimers();
        this.setComponentStep('');
        this.setState({ ...this.initialState });
    };

    openWidget = () => {
        this.setState({ open: true });
    };

    handleClosedWidgetClick = () => {
        if (this.isRestartVariant()) {
            this.setComponentStep('');

            this.setState({
                open: true,
                started: false,
                history: [],
                loading: false,
                error: null,
                displayedNode: null,
                transitioning: false,
                fadeState: 'idle',
            }, () => {
                this.startFlow();
            });

            return;
        }

        this.openWidget();
    };

    startFlow = async () => {
        this.clearTransitionTimers();

        this.setState({
            loading: true,
            error: null,
            history: [],
            displayedNode: null,
            transitioning: false,
            fadeState: 'idle',
        });

        try {
            const node = await this.fetchNode();

            this.setState({
                started: true,
                history: [node],
                displayedNode: node,
                loading: false,
                error: null,
                transitioning: false,
                fadeState: 'idle',
            }, () => {
                this.setComponentStep(node.dataComponentStep);
            });
        } catch (err) {
            this.setState({
                loading: false,
                error: 'Sorry, something went wrong while loading this step.',
            }, () => {
                this.setComponentStep('');
            });
        }
    };

    renderAnswers = (node) => {
        const { urgentHelpContent } = this.props;

        if (!node) return null;

        const hasAnswers = node.answers && node.answers.length > 0;

        return (
            <>
                <div className="answer-list">
                    {hasAnswers ? (
                        node.answers.map((ans, i) => (
                            <button
                                key={`${node.id}-${i}`}
                                className="answer"
                                onClick={() => this.handleSelect(ans)}
                                disabled={this.state.transitioning}
                            >
                                <span className="answer__label">{ans.label}</span>
                                <img
                                    className="answer__icon"
                                    src={
                                        ans.isSearchResult
                                            ? "/content/assets/general/img/ask-ash/Search_Purple.svg"
                                            : "/content/assets/general/img/ask-ash/Chevron_Dark.svg"
                                    }
                                    alt=""
                                />
                            </button>
                        ))
                    ) : (
                        <button
                            className="answer"
                            onClick={this.startOver}
                            disabled={this.state.transitioning}
                        >
                            Start over
                        </button>
                    )}
                </div>

                <div
                    className="ask-ash-urgent-help"
                    dangerouslySetInnerHTML={{ __html: urgentHelpContent }}
                >
                </div>
            </>
        );
    };

    render() {
        const {
            open,
            started,
            history,
            loading,
            error,
            displayedNode,
            fadeState,
            transitioning,
        } = this.state;

        const { title, description, titleWhenOpen, descriptionWhenOpen, variant } = this.props;
        const currentNode = displayedNode;

        return (
            <>
                {open ? (
                    <div className="askAshContainer" ref={this.setContainerRef}>
                        {!started ? (
                            <div className="askAshCard">
                                <img
                                    className="ask-ash-hi-logo"
                                    src="/content/assets/general/img/ask-ash/Hi_Image.png"
                                    alt="Ask Ash logo"
                                />
                                <div>
                                    <div
                                        className="askAshCard__header"
                                        dangerouslySetInnerHTML={{ __html: titleWhenOpen }}
                                    >
                                    </div>

                                    <p className="askAshCard__content">
                                        {descriptionWhenOpen}
                                    </p>

                                    <button
                                        className="primary"
                                        onClick={this.startFlow}
                                        disabled={loading}
                                    >
                                        {loading ? 'Loading…' : 'Start now'}

                                        {!loading && (
                                            <img
                                                className="button-icon button-icon--right"
                                                src="/content/assets/general/img/ask-ash/Arrow_White.png"
                                                alt=""
                                            />
                                        )}
                                    </button>
                                </div>

                                <div className="close-widget" onClick={this.closeWidget}>
                                    <img
                                        className="close-icon"
                                        src="/content/assets/general/img/ask-ash/Close_Dark.svg"
                                        alt="Close"
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className="askAshCard__nav">
                                <div className="nav">
                                    {history.length > 1 && (
                                        <>
                                            <button
                                                className="back-button"
                                                onClick={this.goBack}
                                                disabled={transitioning}
                                            >
                                                <img
                                                    className="button-icon"
                                                    src="/content/assets/general/img/ask-ash/Arrow_White.png"
                                                    alt=""
                                                />
                                                Back
                                            </button>
                                            <button
                                                className="start-over-button"
                                                onClick={this.startOver}
                                                disabled={transitioning}
                                            >
                                                <img
                                                    className="button-icon"
                                                    src="/content/assets/general/img/ask-ash/Restart_Dark.svg"
                                                    alt=""
                                                />
                                                Start over
                                            </button>
                                        </>
                                    )}

                                    <div className="close-widget" onClick={this.closeWidget}>
                                        <img
                                            className="close-icon"
                                            src="/content/assets/general/img/ask-ash/Close_Dark.svg"
                                            alt="Close"
                                        />
                                    </div>
                                </div>

                                {error && <div className="askAshError">{error}</div>}

                                {!currentNode && loading ? (
                                    <div className="askAshCard__initialLoading">
                                        <div className="askAshCard__info">
                                            <h2>Loading…</h2>
                                        </div>
                                    </div>
                                ) : (
                                    <div
                                        className={`askAshCard__contentFade ${fadeState === 'out'
                                            ? 'is-fading-out'
                                            : fadeState === 'in'
                                                ? 'is-fading-in'
                                                : ''
                                            }`}
                                    >
                                        <div className="askAshCard__info">
                                            <img
                                                className="ask-ash-question-logo"
                                                src="/content/assets/general/img/ask-ash/Ask_Ash_Logo.png"
                                                alt="Ask Ash logo"
                                            />
                                            <h2>{currentNode?.question}</h2>
                                        </div>

                                        {currentNode && this.renderAnswers(currentNode)}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                ) : (
                        <div
                            className={`askAshWidget ${this.isRestartVariant() ? 'askAshWidget--restart' : ''}`}
                            onClick={this.handleClosedWidgetClick}
                        >
                            {this.isRestartVariant() ? (
                                <>
                                    <img
                                        className="askAshWidget__restartIcon"
                                        src="/content/assets/general/img/ask-ash/Restart_Dark.svg"
                                        alt=""
                                    />
                                    <span className="askAshWidget__restartText">Restart</span>
                                </>
                            ) : (
                                <>
                                    <img
                                        className="ask-ash-logo"
                                        src="/content/assets/general/img/ask-ash/Ask_Ash_Logo.png"
                                        alt="Ask Ash logo"
                                    />
                                    <div>
                                        <p className="askAshWidget__title">{title}</p>
                                        <p>{description}</p>
                                    </div>
                                </>
                            )}
                        </div>
                )}
            </>
        );
    }
}
